import { useEffect, useMemo, useState } from 'react'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { TabList, Tabs, Tab, TabPanel, TabPanels } from '@wartek-id/tabs'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import { useAPIGetAnnualEvaluationResult } from 'api/kinerja/useAPIKinerja'
import { Header } from 'app/KinerjaShared/components/Header'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { EvaluationResult } from 'app/KinerjaPage/pages/PelaksanaanV2/components/EvaluationResult'
import CardPeriod from 'app/KinerjaShared/components/CardPeriod'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import { formatPeriod } from 'app/KinerjaShared/utils/period'
import BannerStatusPengaliranAkhir from 'app/KinerjaPage/components/BannerStatusPengaliranAkhir'

const PERIOD_1_INDEX = 0,
  PERIOD_2_INDEX = 1,
  ANNUAL_INDEX = 2

export type HasilPenilaianKinerjaProps = {
  isAnnual: boolean
}

export function HasilPenilaianKinerja({
  isAnnual,
}: HasilPenilaianKinerjaProps) {
  const [activeTab, setActiveTab] = useState(0)

  const {
    periodNo: currentPeriod,
    periodYear: currentYear,
    getPeriod,
  } = usePeriodContext()

  const { data: evaluationResultData, isLoading } =
    useAPIGetAnnualEvaluationResult({
      params: { year: currentYear },
    })

  const { trackEvent } = useSKPPelaksanaanTrackEvent()

  const tabsAvailability: Array<{
    locked: boolean
    available: boolean
  }> = useMemo(() => {
    // initial value
    let resolvedValue = [
      { locked: false, available: false },
      { locked: false, available: false },
      { locked: true, available: true },
    ]
    if (!evaluationResultData?.data) return resolvedValue

    evaluationResultData.data.periodResult?.forEach(({ period, locked }) => {
      resolvedValue[period - 1] = {
        locked,
        available: true,
      }
    })

    if (evaluationResultData.data.annualResult) {
      resolvedValue[ANNUAL_INDEX] = {
        available: true,
        locked: evaluationResultData.data.annualResult?.locked,
      }
    }

    return resolvedValue
  }, [evaluationResultData])

  useEffect(() => {
    // Open the annual document if any
    if (
      isAnnual &&
      tabsAvailability[ANNUAL_INDEX].available &&
      !tabsAvailability[ANNUAL_INDEX].locked
    ) {
      setActiveTab(ANNUAL_INDEX)
      return
    }

    // Otherwise, open the period document
    if (
      tabsAvailability[currentPeriod - 1].available &&
      !tabsAvailability[currentPeriod - 1].locked
    ) {
      setActiveTab(currentPeriod - 1)
      return
    }

    // If somehow the current period document is not available,
    // then fallback to the first available document
    const firstAvailable = tabsAvailability.findIndex(
      (isAvailable) => !isAvailable.locked && isAvailable.available
    )
    if (firstAvailable >= 0) setActiveTab(firstAvailable)
  }, [isAnnual, tabsAvailability, currentPeriod])

  useEffect(() => {
    // only call tracker if current doc available
    if (
      tabsAvailability[activeTab].available &&
      !tabsAvailability[activeTab].locked
    ) {
      let selectedPeriodRange = ''

      if (activeTab === ANNUAL_INDEX) {
        selectedPeriodRange = 'Tahunan'
      } else {
        const currentParsedPeriod = getPeriod({
          periodNo: activeTab + 1,
          periodYear: currentYear,
        })
        selectedPeriodRange = formatPeriod(
          currentParsedPeriod.startDate,
          currentParsedPeriod.endDate,
          {
            withYear: false,
          }
        )
      }

      trackEvent({
        activity_type: 'open_hasil_penilaian_kinerja',
        screen_name: 'homepage_pengelolaan',
        attributes: {
          period_selected: selectedPeriodRange,
        },
      })
    }
  }, [activeTab, tabsAvailability, currentYear, getPeriod, trackEvent])

  return (
    <main className="bg-gray-10 min-h-full">
      <header className="bg-white pt-11 pb-6 z-[2] relative">
        <div className="max-w-7xl mx-auto px-8">
          <Header
            title="Hasil Penilaian Kinerja Guru"
            rightNode={
              <Button
                variant="ghost"
                className="flex gap-1.5 !py-4 rounded print:hidden"
              >
                <Icon fontSize="sm" color="informational">
                  help
                </Icon>
                Baca panduan
              </Button>
            }
          />
        </div>
      </header>

      <Tabs selectedIndex={activeTab} onChange={(i) => setActiveTab(i)}>
        <div
          className="bg-white sticky z-[1] top-[4.25rem]"
          style={{
            boxShadow: 'var(--elevation-02)',
          }}
        >
          <div className="max-w-7xl mx-auto px-8 w-full">
            <TabList>
              <Tab
                disabled={tabsAvailability[PERIOD_1_INDEX].locked}
                hidden={!tabsAvailability[PERIOD_1_INDEX].available}
                className="space-x-2"
              >
                <span>
                  <CardPeriod period={1} year={currentYear} withYear={false} />
                </span>
                {tabsAvailability[PERIOD_1_INDEX].locked && <LockIcon />}
              </Tab>
              <Tab
                disabled={tabsAvailability[PERIOD_2_INDEX].locked}
                className="space-x-2"
              >
                <span>
                  <CardPeriod period={2} year={currentYear} withYear={false} />
                </span>
                {tabsAvailability[PERIOD_2_INDEX].locked && <LockIcon />}
              </Tab>
              <Tab
                disabled={tabsAvailability[ANNUAL_INDEX].locked}
                className="space-x-2"
              >
                <span>Tahunan ({currentYear})</span>
                {tabsAvailability[ANNUAL_INDEX].locked && <LockIcon />}
              </Tab>
            </TabList>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-8 py-11 w-full">
          <TabPanels>
            {isLoading ? (
              <div className="flex items-center justify-center px-9 pt-9 gap-5 h-96">
                <Spinner />
                <Text variant="heading-md">Dokumen sedang disiapkan.</Text>
              </div>
            ) : (
              <>
                <TabPanel>
                  <EvaluationResult
                    data={evaluationResultData?.data?.periodResult?.find(
                      (result) => result.period === 1
                    )}
                    period={1}
                    year={currentYear}
                  />
                </TabPanel>
                <TabPanel>
                  <EvaluationResult
                    data={evaluationResultData?.data?.periodResult?.find(
                      (result) => result.period === 2
                    )}
                    period={2}
                    year={currentYear}
                  />
                </TabPanel>
                <TabPanel className="flex flex-col space-y-6">
                  <BannerStatusPengaliranAkhir year={currentYear} />
                  <EvaluationResult
                    data={evaluationResultData?.data?.annualResult}
                    year={currentYear}
                  />
                </TabPanel>
              </>
            )}
          </TabPanels>
        </div>
      </Tabs>
    </main>
  )
}

function LockIcon() {
  return (
    <Icon fontSize="sm" className="!text-xl !leading-5" color="disabled">
      lock_outlined
    </Icon>
  )
}
